import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExplorerTxUrl, CHAIN_NAME } from '@/utils/chain';
import {
  Plus,
  Scissors,
  Upload,
  Loader2,
  ExternalLink,
  FileText,
  UserCheck,
  Clock,
} from 'lucide-react';
import { FileUploadModal } from './FileUploadModal';
import { AddGroomingModal } from './AddGroomingModal';
import { DocumentViewerModal } from './DocumentViewerModal';
import { AssignProfessionalModal } from '../AssignProfessionalModal';
import {
  CreateGroomingRecordRequest,
  GroomingService,
  GroomingRecord,
} from '../../../../api/pets';
import {
  useGroomingRecords,
  useCreateGroomingRecord,
  useUpdateGroomingRecord,
  petRecordsKeys,
} from '../../../../hooks/usePetRecords';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { parseUploadInitResponse, getAbsoluteUploadUrl } from '@/api/documents';
import { useNotifications } from '../../context/NotificationsContext';
import { usePets } from '../../context/PetsContext';

interface GroomingRecordsProps {
  petId: string;
}

const SERVICE_TYPE_KEY_BY_VALUE: Record<string, string> = {
  'Full Grooming Service': 'fullGrooming',
  'Basic Bath & Brush': 'basicBathBrush',
  'Nail Trim Only': 'nailTrimOnly',
  'De-shedding Treatment': 'desheddingTreatment',
  'Puppy/Kitten First Groom': 'puppyFirstGroom',
  Other: 'other',
};

const SERVICE_KEY_BY_VALUE: Record<string, string> = {
  Bath: 'bath',
  Haircut: 'haircut',
  'Nail Trim': 'nailTrim',
  'Ear Cleaning': 'earCleaning',
  Brush: 'brush',
  'De-shedding': 'deshedding',
  'Teeth Brushing': 'teethBrushing',
  'Anal Gland Expression': 'analGlandExpression',
};

export function GroomingRecords({ petId }: GroomingRecordsProps) {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewingRecord, setViewingRecord] = useState<GroomingRecord | null>(
    null
  );
  const [showAssignProfessional, setShowAssignProfessional] = useState(false);
  const [verifyingRecordId, setVerifyingRecordId] = useState<string | null>(null);
  const [verificationRequested, setVerificationRequested] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();
  const {
    data: records = [],
    isLoading,
    error,
    isFetching,
  } = useGroomingRecords(petId);
  const createRecordMutation = useCreateGroomingRecord(petId);
  const updateGroomingMutation = useUpdateGroomingRecord(petId);
  const { addNotification } = useNotifications();
  const { pets } = usePets();
  const petName = pets.find((p) => p.id === petId)?.name ?? 'Your pet';

  const handleRequestVerification = (recordId: string) => {
    setVerifyingRecordId(recordId);
    setShowAssignProfessional(true);
  };

  const handleProfessionalAssigned = async (professional: { id: string; name: string; type: string; source: 'existing' | 'invited'; email?: string | null; phone?: string | null }) => {
    if (!verifyingRecordId) return;
    try {
      await apiClient.post(`/pets/${petId}/request-verification`, {
        recordId: verifyingRecordId,
        recordTable: 'grooming_records',
        professionalId: professional.id || undefined,
        professionalName: professional.name,
        professionalEmail: professional.email || undefined,
        professionalType: professional.type || 'grooming',
      });
      setVerificationRequested((prev) => new Set(prev).add(verifyingRecordId));
      addNotification({
        type: 'document_shared',
        title: 'Verification Requested',
        message: `Verification request sent to ${professional.name}`,
      });
    } catch {
      addNotification({
        type: 'edit_requested',
        title: 'Request Failed',
        message: 'Could not send verification request. Please try again.',
      });
    }
    setShowAssignProfessional(false);
    setVerifyingRecordId(null);
  };

  const handleAddRecord = async (recordData: {
    serviceType: string;
    date: string;
    groomerName: string;
    clinicName: string;
    coatCondition: string;
    behavioralObservations: string;
    groomerNotes: string;
    services: string[];
  }) => {
    try {
      const request: CreateGroomingRecordRequest = {
        serviceType:
          recordData.serviceType as CreateGroomingRecordRequest['serviceType'],
        serviceDate: recordData.date,
        groomerName: recordData.groomerName,
        salonName: recordData.clinicName,
        servicesProvided: recordData.services as GroomingService[],
        coatCondition: recordData.coatCondition,
        behavioralObservations: recordData.behavioralObservations,
        groomerNotes: recordData.groomerNotes || undefined,
        verified: false,
      };

      await createRecordMutation.mutateAsync(request);
      addNotification({
        type: 'record_added',
        title: t(
          'features.healthRecords.recordAddedTitle',
          'Record salute aggiunto'
        ),
        message: t('features.healthRecords.groomingRecordAddedMessage', {
          serviceType: recordData.serviceType,
          petName,
          defaultValue:
            'Record toelettatura ({{serviceType}}) aggiunto per {{petName}}.',
        }),
        petId,
        petName,
        icon: '✂️',
        source: 'E-Passport',
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Failed to create grooming record:', err);
      alert(
        t(
          'features.healthRecords.errors.addGroomingFailed',
          'Aggiunta record toelettatura non riuscita. Riprova.'
        )
      );
    }
  };

  // Only show loading spinner on initial load (no cached data)
  if (isLoading && records.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (error && records.length === 0) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4">
        <p
          className="text-red-700 dark:text-red-300"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t(
            'features.healthRecords.failedToLoadGroomingRecords',
            'Caricamento record toelettatura non riuscito'
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3
            className="text-xl mb-1 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('features.healthRecords.groomingRecordsTitle')}
          </h3>
          <p
            className="text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.groomingDesc')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isFetching && (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          )}
          <button
            onClick={() => setShowAddModal(true)}
            disabled={createRecordMutation.isPending}
            className="flex items-center gap-2 bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {createRecordMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {t('features.healthRecords.addRecord')}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {records.length === 0 && (
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/8 rounded-lg p-8 text-center">
          <Scissors className="w-12 h-12 text-gray-400 dark:text-white/40 mx-auto mb-4" />
          <p
            className="text-gray-600 dark:text-white/70 mb-2"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {t('features.healthRecords.noGroomingYet')}
          </p>
          <p
            className="text-sm text-gray-500 dark:text-white/50"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.addFirstGrooming')}
          </p>
        </div>
      )}

      {/* Records List */}
      <div className="space-y-4">
        {records.map((record) => (
          <div
            key={record.id}
            className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Scissors className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <h4
                    className="text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                  >
                    {t(
                      `features.healthRecords.addGroomingModal.${SERVICE_TYPE_KEY_BY_VALUE[record.serviceType] || 'other'}`,
                      record.serviceType
                    )}
                  </h4>
                </div>
                <div
                  className="text-sm text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {new Date(record.serviceDate).toLocaleDateString()} •{' '}
                  {record.groomerName} • {record.salonName}
                </div>
              </div>
              {record.verified && (
                <span
                  className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  ✂️{' '}
                  {t(
                    'features.healthRecords.groomerVerified',
                    'Toelettatore verificato'
                  )}
                </span>
              )}
            </div>

            {/* Services Provided */}
            <div className="mb-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.servicesProvided',
                  'Servizi forniti'
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {record.servicesProvided.map((service, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t(
                      `features.healthRecords.addGroomingModal.${SERVICE_KEY_BY_VALUE[service] || 'other'}`,
                      service
                    )}
                  </span>
                ))}
              </div>
            </div>

            {/* Coat Condition */}
            <div className="mb-3">
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.coatConditionLabel',
                  'Condizione mantello'
                )}
              </div>
              <div
                className="text-sm text-gray-900 dark:text-white/90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {record.coatCondition}
              </div>
            </div>

            {/* Behavioral Observations */}
            <div className="mb-3">
              <div
                className="text-sm text-gray-600 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.behavioralObservationsLabel',
                  'Osservazioni comportamentali'
                )}
              </div>
              <div
                className="text-sm text-gray-900 dark:text-white/90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {record.behavioralObservations}
              </div>
            </div>

            {/* Groomer Notes */}
            {record.groomerNotes && (
              <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 mb-4">
                <div
                  className="text-sm text-gray-600 dark:text-white/50 mb-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.groomerNotesLabel',
                    'Note del toelettatore'
                  )}
                </div>
                <div
                  className="text-sm text-gray-900 dark:text-white/90"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {record.groomerNotes}
                </div>
              </div>
            )}

            {/* Blockchain Status */}
            <div className="mb-4">
              {record.txSignature ? (
                <a
                  href={getExplorerTxUrl(record.txSignature)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  {t(
                    'features.healthRecords.verifiedOnSolana',
                    `Verified on ${CHAIN_NAME}`,
                    { chain: CHAIN_NAME }
                  )}
                  <span className="font-mono text-gray-500 dark:text-white/50">
                    · {record.txSignature.substring(0, 8)}…{record.txSignature.substring(record.txSignature.length - 4)}
                  </span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                  {t(
                    'features.healthRecords.awaitingBlockchainSync',
                    'In attesa sincronizzazione blockchain'
                  )}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedRecord(record.id);
                  setShowFileUpload(true);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <Upload className="w-4 h-4" />
                {t('features.healthRecords.attachPhotos', 'Allega foto')}
              </button>
              <button
                onClick={() => {
                  setViewingRecord(record);
                  setShowDocumentViewer(true);
                }}
                disabled={!record.documentHash}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded transition-colors ${
                  record.documentHash
                    ? 'border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90'
                    : 'border-gray-200 dark:border-white/10 text-gray-400 dark:text-white/30 cursor-not-allowed'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <FileText className="w-4 h-4" />
                {t('features.healthRecords.viewDocument', 'View Document')}
              </button>
              {!record.verified && !verificationRequested.has(record.id) && (
                <button
                  onClick={() => handleRequestVerification(record.id)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border-2 border-green-500 dark:border-green-400 text-green-600 dark:text-green-400 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <UserCheck className="w-4 h-4" />
                  Request Verification
                </button>
              )}
              {!record.verified && verificationRequested.has(record.id) && (
                <span
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-yellow-400 dark:border-yellow-500/50 text-yellow-600 dark:text-yellow-400 rounded bg-yellow-50 dark:bg-yellow-900/10"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <Clock className="w-4 h-4" />
                  Verification Pending
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
        <h4
          className="mb-2 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
        >
          {t(
            'features.healthRecords.whyGroomingMatters',
            'Perché i record di toelettatura sono importanti'
          )}
        </h4>
        <p
          className="text-sm text-gray-700 dark:text-blue-100"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t(
            'features.healthRecords.whyGroomingBody',
            'I record di toelettatura aiutano a monitorare mantello, comportamento e benessere generale nel tempo.'
          )}
        </p>
      </div>

      {showFileUpload && selectedRecord && (
        <FileUploadModal
          onClose={() => {
            setShowFileUpload(false);
            setSelectedRecord(null);
          }}
          category="Grooming Report"
          onUpload={async (fileData) => {
            if (!selectedRecord) return;
            const groomingId = selectedRecord;
            const contentType =
              fileData.file.type || 'application/octet-stream';
            const initRes = await apiClient.post('/documents/upload-init', {
              filename: fileData.file.name,
              contentType,
              folder: 'grooming-documents',
              ttlSeconds: 900,
            });
            const raw = initRes.data as
              | {
                  uploadUrl?: string;
                  url?: string;
                  key?: string;
                  body?: unknown;
                  data?: {
                    uploadUrl?: string;
                    url?: string;
                    key?: string;
                  };
                }
              | undefined;
            const body =
              typeof raw?.body === 'string'
                ? (() => {
                    try {
                      return JSON.parse(raw.body) as Record<string, unknown>;
                    } catch {
                      return null;
                    }
                  })()
                : raw?.body && typeof raw.body === 'object'
                  ? (raw.body as Record<string, unknown>)
                  : null;
            const nested =
              raw?.data && typeof raw.data === 'object' ? raw.data : null;
            const uploadUrl =
              (typeof raw?.uploadUrl === 'string' && raw.uploadUrl) ||
              (typeof raw?.url === 'string' && raw.url) ||
              (typeof nested?.uploadUrl === 'string' && nested.uploadUrl) ||
              (typeof nested?.url === 'string' && nested.url) ||
              (body && typeof body.uploadUrl === 'string'
                ? body.uploadUrl
                : '') ||
              (body && typeof body.url === 'string' ? body.url : '');
            const key =
              (typeof raw?.key === 'string' && raw.key) ||
              (typeof nested?.key === 'string' && nested.key) ||
              (body && typeof body.key === 'string' ? body.key : '');

            if (!uploadUrl || !key) {
              throw new Error('Upload init did not return a valid upload URL');
            }

            if (/^https?:\/\//i.test(uploadUrl)) {
              const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                body: fileData.file,
              });
              if (!putRes.ok) {
                throw new Error(`Upload failed with status ${putRes.status}`);
              }
            } else {
              const relativeUrl = uploadUrl.startsWith('/')
                ? uploadUrl
                : `/${uploadUrl}`;
              try {
                await apiClient.put(relativeUrl, fileData.file, {
                  headers: { 'Content-Type': contentType },
                });
              } catch (err) {
                const status = (err as { response?: { status?: number } })
                  ?.response?.status;
                if (status !== 405) throw err;
                await apiClient.post(relativeUrl, fileData.file, {
                  headers: { 'Content-Type': contentType },
                });
              }
            }
            await updateGroomingMutation.mutateAsync({
              groomingId: selectedRecord,
              data: { documentHash: key },
            });
          }}
        />
      )}

      {showDocumentViewer && viewingRecord?.documentHash && (
        <DocumentViewerModal
          onClose={() => {
            setShowDocumentViewer(false);
            setViewingRecord(null);
          }}
          documentHash={viewingRecord.documentHash}
          documentTitle={`${viewingRecord.serviceType} - ${viewingRecord.groomerName}`}
        />
      )}

      {showAddModal && (
        <AddGroomingModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddRecord}
        />
      )}

      {showAssignProfessional && (
        <AssignProfessionalModal
          isOpen={showAssignProfessional}
          onClose={() => {
            setShowAssignProfessional(false);
            setVerifyingRecordId(null);
          }}
          professionalType="grooming"
          professionalLabel="Groomer"
          petId={petId}
          onAssign={handleProfessionalAssigned}
        />
      )}
    </div>
  );
}
